import { useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Home, Grid3X3, Wallet, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { GlobalTopBar } from './GlobalTopBar'
import { GlobalAnnouncementBanner } from './GlobalAnnouncementBanner'
import { ScrollContext } from './ScrollContext'

const tabs = [
  { path: '/', icon: Home, label: '首頁', labelEn: 'Home' },
  { path: '/rooms', icon: Grid3X3, label: '房間', labelEn: 'Rooms' },
  { path: '/wallet', icon: Wallet, label: '錢包', labelEn: 'Wallet' },
  { path: '/profile', icon: User, label: '我的', labelEn: 'Profile' },
]

export function MainLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLElement>(null)

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Global Top Bar + floating announcement anchor */}
      <div className="flex-shrink-0 relative z-40">
        <GlobalTopBar />
        {/* Banner floats below nav via absolute positioning — no layout shift */}
        <GlobalAnnouncementBanner />
      </div>

      {/* Main content area with scroll context */}
      <ScrollContext.Provider value={scrollRef}>
        <main ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-hide">
          <Outlet />
        </main>
      </ScrollContext.Provider>

      {/* Bottom Tab Navigation */}
      <nav className="flex-shrink-0 relative z-50">
        {/* Gradient fade above nav */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background/80 to-transparent pointer-events-none" />

        {/* Nav bar with glass effect */}
        <div className="relative bg-background/95 backdrop-blur-xl border-t border-foreground/[0.06] safe-area-bottom">
          {/* Gold accent line */}
          <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

          <div className="flex justify-around items-center h-16">
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              const Icon = tab.icon

              return (
                <motion.button
                  key={tab.path}
                  onClick={() => navigate(tab.path)}
                  whileTap={{ scale: 0.95 }}
                  className="group relative flex flex-col items-center justify-center w-full h-full touch-target"
                >
                  {/* Active Indicator - Gold glow line */}
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute top-0 w-12 h-1 rounded-b-full bg-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.5)]"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}

                  {/* Icon container */}
                  <div className={cn(
                    'relative transition-all duration-300 transform',
                    isActive ? 'scale-110 -translate-y-1' : 'group-active:scale-95'
                  )}>
                    {/* Glow effect behind active icon */}
                    {isActive && (
                      <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-md" />
                    )}
                    <Icon
                      className={cn(
                        'relative w-6 h-6 mb-1 transition-colors duration-300',
                        isActive ? 'text-amber-500 stroke-[2.5px]' : 'text-muted-foreground group-hover:text-foreground'
                      )}
                    />
                  </div>

                  {/* Label */}
                  <span className={cn(
                    "text-[10px] font-semibold transition-colors duration-300",
                    isActive ? 'text-amber-500' : 'text-muted-foreground'
                  )}>
                    {tab.label}
                  </span>
                </motion.button>
              )
            })}
          </div>
        </div>
      </nav>
    </div>
  )
}
